import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';
import { PRODUCTION_URL } from '../productionLink/productionLink';

const BarMain = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [stats, setStats] = useState({});
  const [activeOrders, setActiveOrders] = useState([]);
  const [topDrinks, setTopDrinks] = useState([]);

  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444'
  };

  const isMobile = windowWidth <= 768;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([fetchStats(), fetchActiveOrders()]);
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const data = await apiGet('/admin/bar/operations/stats');
      if (data.success) {
        setStats(data.data.todaySales || {});
        setTopDrinks(data.data.topDrinks || []);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchActiveOrders = async () => {
    try {
      const data = await apiGet('/admin/bar/operations/active-orders');
      if (data.success) {
        setActiveOrders(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  // Download bill function
  const handleDownloadBill = async (orderId, orderNumber, e) => {
    e.stopPropagation();

    try {
      window.open(
        `${PRODUCTION_URL}/api/admin/bar/bills/${orderId}/download`,
        '_blank'
      );
    } catch (error) {
      console.error('Error downloading bill:', error);
      alert('Error downloading bill');
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      pending: colors.warning,
      preparing: colors.accent,
      ready: colors.success,
      served: colors.primary
    };
    return statusColors[status] || colors.textLight;
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    return `${diffHours}h ago`;
  };

  const styles = {
    pageHeader: {
      marginBottom: isMobile ? '16px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    pageTitle: {
      fontSize: isMobile ? '20px' : '28px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '6px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    pageSubtitle: {
      fontSize: isMobile ? '12px' : '14px',
      color: colors.textLight,
      fontWeight: '500'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
      gap: isMobile ? '12px' : '20px',
      marginBottom: isMobile ? '20px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    statCard: {
      background: colors.white,
      padding: isMobile ? '20px' : '24px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 4px 12px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`
    },
    contentCard: {
      background: colors.white,
      padding: isMobile ? '16px' : '24px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`,
      margin: isMobile ? '0 4px 16px 4px' : '0 0 24px 0'
    },
    quickActionGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
      gap: isMobile ? '12px' : '16px',
      padding: isMobile ? '0 4px' : '0'
    },
    quickActionCard: {
      background: `linear-gradient(135deg, ${colors.primary}, ${colors.accent})`,
      padding: isMobile ? '20px' : '24px',
      borderRadius: isMobile ? '14px' : '16px',
      color: colors.white,
      textAlign: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 12px rgba(45, 80, 22, 0.2)',
      border: 'none'
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div>Loading bar dashboard...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Page Header */}
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>
          <span>🍸</span> Bar Management Dashboard
        </h1>
        <p style={styles.pageSubtitle}>Real-time bar operations and performance overview</p>
      </div>

      {/* Statistics Grid */}
      <div style={styles.statsGrid}>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.success}` }}>
          <div style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: '800', color: colors.success }}>
            ₹{(stats.total_revenue || 0).toLocaleString('en-IN')}
          </div>
          <div style={{ fontSize: isMobile ? '11px' : '12px', color: colors.textLight, textTransform: 'uppercase', fontWeight: '600' }}>
            Today's Revenue
          </div>
        </div>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.primary}` }}>
          <div style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: '800', color: colors.primary }}>
            {stats.total_orders || 0}
          </div>
          <div style={{ fontSize: isMobile ? '11px' : '12px', color: colors.textLight, textTransform: 'uppercase', fontWeight: '600' }}>
            Total Orders
          </div>
        </div>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.accent}` }}>
          <div style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: '800', color: colors.accent }}>
            {activeOrders.length}
          </div>
          <div style={{ fontSize: isMobile ? '11px' : '12px', color: colors.textLight, textTransform: 'uppercase', fontWeight: '600' }}>
            Active Orders
          </div>
        </div>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.warning}` }}>
          <div style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: '800', color: colors.warning }}>
            {stats.items_sold || 0}
          </div>
          <div style={{ fontSize: isMobile ? '11px' : '12px', color: colors.textLight, textTransform: 'uppercase', fontWeight: '600' }}>
            Items Sold
          </div>
        </div>
      </div>

      {/* Active Bar Orders */}
      <div style={styles.contentCard}>
        <h2 style={{ fontSize: isMobile ? '16px' : '20px', fontWeight: '700', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>🔥</span> Active Bar Orders
        </h2>
        {activeOrders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>✨</div>
            <div style={{ fontSize: '14px' }}>No active bar orders at the moment</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {activeOrders.slice(0, 5).map(order => (
              <div
                key={order.id}
                style={{
                  padding: isMobile ? '12px' : '14px',
                  background: colors.lightGray,
                  borderRadius: '8px',
                  borderLeft: `4px solid ${getStatusColor(order.order_status)}`,
                  transition: 'all 0.3s ease'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <div>
                    <strong style={{ fontSize: isMobile ? '14px' : '16px' }}>{order.order_number}</strong>
                    {order.table_number && (
                      <span style={{ marginLeft: '8px', fontSize: '12px', color: colors.textLight }}>
                        Table {order.table_number}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ fontSize: '12px', color: colors.textLight, marginBottom: '8px' }}>
                  {order.items_summary}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: colors.primary }}>
                    ₹{parseFloat(order.total_amount || 0).toLocaleString('en-IN')}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ fontSize: '11px', color: colors.textLight }}>
                      {formatTime(order.created_at)}
                    </div>
                    <button
                      onClick={(e) => handleDownloadBill(order.id, order.order_number, e)}
                      style={{
                        padding: '6px 12px',
                        background: colors.primary,
                        color: colors.white,
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = colors.accent;
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = colors.primary;
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      <span>📄</span>
                      <span>Bill</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {activeOrders.length > 5 && (
          <button
            onClick={() => window.location.href = '/admin/bar/operations'}
            style={{
              width: '100%',
              marginTop: '12px',
              padding: '10px',
              background: colors.primary,
              color: colors.white,
              border: 'none',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.background = colors.accent}
            onMouseLeave={(e) => e.target.style.background = colors.primary}
          >
            View All Orders ({activeOrders.length})
          </button>
        )}
      </div>

      {/* Top Drinks Today */}
      {topDrinks && topDrinks.length > 0 && (
        <div style={styles.contentCard}>
          <h2 style={{ fontSize: isMobile ? '16px' : '20px', fontWeight: '700', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>🏆</span> Top Selling Drinks Today
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {topDrinks.slice(0, 5).map((drink, index) => (
              <div key={index} style={{
                padding: isMobile ? '12px' : '14px',
                background: colors.lightGray,
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: colors.primary,
                    color: colors.white,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: '700',
                    flexShrink: 0
                  }}>
                    {index + 1}
                  </div>
                  <div>
                    <div style={{ fontSize: isMobile ? '13px' : '14px', fontWeight: '600', color: colors.textDark }}>
                      {drink.item_name}
                    </div>
                    <div style={{ fontSize: '11px', color: colors.textLight }}>
                      Sold: {drink.quantity_sold}
                    </div>
                  </div>
                </div>
                <div style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: '700', color: colors.success }}>
                  ₹{parseFloat(drink.revenue || 0).toLocaleString('en-IN')}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div style={styles.contentCard}>
        <h2 style={{ fontSize: isMobile ? '16px' : '20px', fontWeight: '700', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>⚡</span> Quick Actions
        </h2>
        <div style={styles.quickActionGrid}>
          <button
            style={styles.quickActionCard}
            onClick={() => window.location.href = '/admin/bar/operations'}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-4px)';
              e.target.style.boxShadow = '0 8px 20px rgba(45, 80, 22, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(45, 80, 22, 0.2)';
            }}
          >
            <div style={{ fontSize: isMobile ? '32px' : '40px', marginBottom: '8px' }}>🍹</div>
            <div style={{ fontSize: isMobile ? '13px' : '15px', fontWeight: '700' }}>Operations</div>
            <div style={{ fontSize: '11px', marginTop: '4px', opacity: 0.9 }}>Manage Orders</div>
          </button>

          <button
            style={styles.quickActionCard}
            onClick={() => window.location.href = '/admin/bar/menu'}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-4px)';
              e.target.style.boxShadow = '0 8px 20px rgba(45, 80, 22, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(45, 80, 22, 0.2)';
            }}
          >
            <div style={{ fontSize: isMobile ? '32px' : '40px', marginBottom: '8px' }}>📋</div>
            <div style={{ fontSize: isMobile ? '13px' : '15px', fontWeight: '700' }}>Menu</div>
            <div style={{ fontSize: '11px', marginTop: '4px', opacity: 0.9 }}>Drinks & Cocktails</div>
          </button>

          <button
            style={styles.quickActionCard}
            onClick={() => window.location.href = '/admin/bar/inventory'}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-4px)';
              e.target.style.boxShadow = '0 8px 20px rgba(45, 80, 22, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(45, 80, 22, 0.2)';
            }}
          >
            <div style={{ fontSize: isMobile ? '32px' : '40px', marginBottom: '8px' }}>🍾</div>
            <div style={{ fontSize: isMobile ? '13px' : '15px', fontWeight: '700' }}>Inventory</div>
            <div style={{ fontSize: '11px', marginTop: '4px', opacity: 0.9 }}>Stock Management</div>
          </button>

          <button
            style={styles.quickActionCard}
            onClick={() => window.location.href = '/admin/bar/reports'}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-4px)';
              e.target.style.boxShadow = '0 8px 20px rgba(45, 80, 22, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(45, 80, 22, 0.2)';
            }}
          >
            <div style={{ fontSize: isMobile ? '32px' : '40px', marginBottom: '8px' }}>📊</div>
            <div style={{ fontSize: isMobile ? '13px' : '15px', fontWeight: '700' }}>Reports</div>
            <div style={{ fontSize: '11px', marginTop: '4px', opacity: 0.9 }}>Sales Analytics</div>
          </button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default BarMain;